import React, { useRef, useEffect, useMemo } from "react";
import { Container, Graphics, Text } from "@pixi/react";
import { TextStyle } from "pixi.js";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { closePaytable } from "../../../../State/actions";
import { selectPaytableActive } from "../../../../State/selectors";
import anime from "animejs";
import { PixiSettings } from "../../../../pixiSettings";
import { FONT_CONFIG } from "../../../../textureConfigs";
import ButtonMenuClose from "../Buttons/ButtonMenuClose";
import PaytableSlider from "./PaytableSlider";

const Paytable = ({ toolbar }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const paytableActive = useSelector(selectPaytableActive);
  const {
    dimensions,
    device,
    orientation,
    uiSymbolScale,
  } = useDimensions();
  const containerRef = useRef(null);
  const animationRef = useRef(null);

  const handleClose = () => {
    dispatch(closePaytable());
  };

  // Calculate viewport dimensions
  const viewportConfig = useMemo(() => {
    // Get viewport configuration based on device and orientation
    const getViewportConfig = () => {
      // MOBILE PORTRAIT
      if (device === PixiSettings.DEVICE.MOBILE) {
        if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
          return {
            maxWidth: dimensions.width * 0.75,
            paddingTop: dimensions.height / 5.75,
            paddingBottom: 0,
            titleFontSizeMultiplier: 1.35,
            closeButtonOffsetMultiplier: 1.65,
          };
        } else {
          // MOBILE LANDSCAPE
          return {
            maxWidth: toolbar.width * 0.8,
            paddingTop: dimensions.height / 3.45,
            paddingBottom: 0,
            titleFontSizeMultiplier: 1.3,
            closeButtonOffsetMultiplier: 1.35,
          };
        }
        // TABLET PORTRAIT
      } else if (device === PixiSettings.DEVICE.TABLET) {
        if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
          return {
            maxWidth: dimensions.width * 0.75,
            paddingTop: dimensions.height /5.5,
            paddingBottom: 0,
            titleFontSizeMultiplier: 1.45,
            closeButtonOffsetMultiplier: 1.75,
          };
        } else {
          // TABLET LANDSCAPE
          return {
            maxWidth: toolbar.width * 0.65,
            paddingTop: dimensions.height / 4.75,
            paddingBottom: 0,
            titleFontSizeMultiplier: 1.0,
            closeButtonOffsetMultiplier: 1.65,
          };
        }
      } else {
        // DESKTOP
        return {
          maxWidth: toolbar.width * 0.575,
          paddingTop: dimensions.height / 3.25,
          paddingBottom: 0,
          titleFontSizeMultiplier: 1.15,
          closeButtonOffsetMultiplier: 1.85,
        };
      }
    };

    const config = getViewportConfig();
    const viewportHeight =
      dimensions.height - config.paddingTop - config.paddingBottom;

    // Center position
    const viewportX = dimensions.width / 2;
    const viewportY = config.paddingTop;

    return {
      maxWidth: config.maxWidth,
      viewportHeight,
      viewportX,
      viewportY,
      paddingTop: config.paddingTop,
      paddingBottom: config.paddingBottom,
      titleFontSizeMultiplier: config.titleFontSizeMultiplier,
      closeButtonOffsetMultiplier: config.closeButtonOffsetMultiplier,
    };
  }, [dimensions, device, orientation, toolbar.width]);

  // Calculate title font size (needed for positioning calculations)
  const baseTitleFontSize = 56;
  const titleFontSize = useMemo(
    () =>
      Math.round(
        baseTitleFontSize *
          viewportConfig.titleFontSizeMultiplier *
          uiSymbolScale
      ),
    [viewportConfig.titleFontSizeMultiplier, uiSymbolScale]
  );

  // Title text style
  const titleStyle = useMemo(
    () =>
      new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: titleFontSize,
        fontWeight: "bold",
        fill: 0xffffff, // White color
        align: "center",
      }),
    [titleFontSize]
  );


  // Calculate close button position: right side of viewport, vertically aligned with title
  const closeButtonPosition = useMemo(() => {
    const titleY = viewportConfig.viewportY - titleFontSize * 3.65; // Same Y as title
    const viewportRightEdge =
      viewportConfig.viewportX + viewportConfig.maxWidth / 2;

    // Center button vertically with the title
    const buttonCenterY = titleY + titleFontSize / 1.5;

    return {
      x: Math.round(viewportRightEdge),
      y: Math.round(buttonCenterY),
    };
  }, [
    viewportConfig.viewportX,
    viewportConfig.viewportY,
    viewportConfig.maxWidth,
    titleFontSize,
  ]);

  useEffect(() => {
    if (containerRef.current) {
      if (paytableActive) {
        animationRef.current = anime({
          targets: containerRef.current,
          alpha: 1,
          duration: 200,
          easing: "easeOutQuad",
        });
      } else {
        animationRef.current = anime({
          targets: containerRef.current,
          alpha: 0,
          duration: 200,
          easing: "easeInQuad",
        });
      }
    }
  }, [paytableActive]);

  return (
    <Container
      ref={containerRef}
      name="secondary_paytable_overlay"
      zIndex={10}
      eventMode={paytableActive ? "static" : "none"}
    >
      <Graphics
        name="secondary_paytable_background"
        draw={(g) => {
          g.clear();
          g.beginFill(0x000000, 1);
          g.drawRect(0, 0, dimensions.width, dimensions.height);
          g.endFill();
        }}
        alpha={0.9}
        eventMode={paytableActive ? "static" : "none"}
        onpointertap={(e) => e.stopPropagation()}
      />

      {/* Title */}
      <Text
        text={t("paytable_title", { ns: "common" })}
        style={titleStyle}
        anchor={{ x: 0.5, y: 0 }}
        x={Math.round(dimensions.width / 2)}
        y={Math.round(viewportConfig.viewportY - titleFontSize * 3.65)}
        resolution={2}
      />

      {/* Title separator line */}
      <Graphics
        name="secondary_paytable_title_line"
        draw={(g) => {
          g.clear();
          g.beginFill(0xfeff00, 1); // Same yellow color as title
          const lineWidth =
            viewportConfig.maxWidth + viewportConfig.maxWidth * 0.1;
          g.drawRect(0, 0, lineWidth, 3);
          g.endFill();
        }}
        x={Math.round(
          dimensions.width / 2 -
            (viewportConfig.maxWidth + viewportConfig.maxWidth * 0.1) / 2
        )}
        y={Math.round(viewportConfig.viewportY - titleFontSize * 2.0)}
      />

      {/* Viewport container with slider - starts right below the separator line */}
      <Container
        name="secondary_paytable_viewport"
        x={Math.round(viewportConfig.viewportX)}
        y={Math.round(viewportConfig.viewportY - titleFontSize * 2.0 + 3)}
      >
        <PaytableSlider viewportConfig={viewportConfig} totalSlides={7} />
      </Container>

      {/* Close Button */}
      <ButtonMenuClose
        onClose={handleClose}
        x={closeButtonPosition.x}
        y={closeButtonPosition.y}
      />
    </Container>
  );
};

export default Paytable;
